import { useCallback, useRef, useState } from 'react';
import { useCurrentUser } from '../../shared/auth';
import { useUploadMediaMutation } from '../../shared/data';
import { useCreateQuestion } from '../../features/audit-whiz/hooks/useCreateQuestion';
import { publishReviewQuestion } from '../../features/audit-whiz/logic/publishReviewQuestion';
import {
  EMPTY_CRITERIA_ATTACHMENTS,
  type CriteriaAttachments,
} from '../../features/audit-whiz/components/AuditCriteriaStep';
import type { ReviewQuestion } from '../../features/audit-whiz/logic/reviewTypes';

/** Fallback copy when a thrown value carries no usable message. */
const DEFAULT_PUBLISH_ERROR = 'Failed to publish question';

/** Extracts a raw message from an unknown thrown value (Error, string, or
 *  other) for the error banner. Localized copy is the component's job. */
function toPublishErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message || DEFAULT_PUBLISH_ERROR;
  if (typeof err === 'string' && err.length > 0) return err;
  return DEFAULT_PUBLISH_ERROR;
}

export interface UseReviewPublishResult {
  /** Publishes the given draft (media upload + create). Fire-and-forget from a
   *  click handler; calls `onPublished(id, questionId)` on success and leaves
   *  the draft alone on failure (the error surfaces via `publishError`). */
  readonly runPublish: (
    target: ReviewQuestion,
    attachments: CriteriaAttachments,
    onPublished: (id: string, questionId: string) => void,
  ) => void;
  readonly isPublishing: boolean;
  readonly publishError: string | null;
  readonly clearPublishError: () => void;
}

/**
 * Page-level orchestration for "Save & publish": owns the create-question
 * mutation, the media-upload mutation, and the current-user identity, and runs
 * {@link publishReviewQuestion} (upload → map → create). Lives beside the page
 * (not under a feature `hooks/` dir) because it performs the async
 * orchestration the review wizard hook deliberately does not.
 */
export function useReviewPublish(): UseReviewPublishResult {
  const { programId, alias, businessUnit } = useCurrentUser();
  const { publish } = useCreateQuestion();
  const [uploadMedia] = useUploadMediaMutation();

  // Publish-WIDE state: `publishReviewQuestion` runs the media upload BEFORE
  // the create call, so neither the busy flag nor the error can come from the
  // create mutation alone — a long upload (or an upload failure) would
  // otherwise leave `isPublishing` false (button re-enabled → double submit)
  // and `publishError` null (upload failures swallowed → silent no-op). This
  // hook owns both, spanning the whole upload+create operation.
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Re-entrancy guard: a ref (not state) so a second click within the same
  // render — before `setIsPublishing(true)` has committed — still sees the
  // in-flight flag and bails, preventing a second concurrent publish.
  const inFlightRef = useRef(false);

  const clearPublishError = useCallback(() => setPublishError(null), []);

  const runPublish = useCallback(
    (
      target: ReviewQuestion,
      attachments: CriteriaAttachments,
      onPublished: (id: string, questionId: string) => void,
    ): void => {
      // Ignore re-entrant clicks while a publish is already running (covers the
      // whole upload+create span, not just the create mutation's pending flag).
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsPublishing(true);
      setPublishError(null);

      void publishReviewQuestion(target, attachments ?? EMPTY_CRITERIA_ATTACHMENTS, {
        programId,
        createdBy: alias,
        businessUnit: businessUnit ?? '',
        uploadMedia,
        createQuestion: publish,
      })
        .then((response) => {
          // Mark the ORIGINATING draft by id (safe no-op if it moved/was
          // deleted while the request was in flight).
          onPublished(target.id, response.questionId);
        })
        .catch((err: unknown) => {
          // Surface failures from ANY phase — media upload OR create. Sourcing
          // the error here (not from the create mutation) is what makes an
          // upload rejection reach the banner instead of being swallowed.
          setPublishError(toPublishErrorMessage(err));
        })
        .finally(() => {
          inFlightRef.current = false;
          setIsPublishing(false);
        });
    },
    [programId, alias, businessUnit, uploadMedia, publish],
  );

  return { runPublish, isPublishing, publishError, clearPublishError };
}
